type ChatInstance = ReturnType<typeof useChat>;
type BubbleState = 'closed' | 'ai-chat' | 'inbox';

const globalChatInstance = ref<ChatInstance | null>(null);
export const setGlobalChatInstance = (chatInstanceParam: ChatInstance) => {
  globalChatInstance.value = chatInstanceParam;
};

export const useAiChatWidget = () => {
  // Use useState for serializable shared state
  const bubbleState = useState<BubbleState>('aiChatWidget.bubbleState', () => 'closed');
  const loadDrawer = useState<boolean>('aiChatWidget.loadDrawer', () => false);

  // Handles internal lazy loading of the drawer, so that the drawer is not loaded until the user hovers over the bubble.
  const handleFirstHover = () => {
    loadDrawer.value = true;
  };

  const { $ld } = useNuxtApp();

  const getFeatureFlag = (flagName: string): boolean => {
    try {
      const ldClient = $ld as unknown as { flags?: Record<string, boolean> };
      return ldClient?.flags?.[flagName] ?? false;
    } catch {
      return false;
    }
  };

  const aiChatEnabled = computed(() => getFeatureFlag('cmsAiChat'));
  const aiContactFormEnabled = computed(() => getFeatureFlag('cmsContactForm'));

  const handleStateChange = (state: BubbleState) => {
    bubbleState.value = state;
  };

  const handleDrawerClose = () => {
    bubbleState.value = 'closed';
  };

  const isOpen = computed(() => {
    return bubbleState.value !== 'closed';
  });

  const openAiChat = () => {
    if (!loadDrawer.value) {
      loadDrawer.value = true;
    }
    handleStateChange('ai-chat');
  };

  const sendMessage = async (message: string, customData: Record<string, unknown> = {}) => {
    if (!globalChatInstance.value) {
      console.error('Chat instance not initialized');
      return false;
    }

    try {
      await globalChatInstance.value.sendMessage(message, customData);
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  };

  return {
    bubbleState,
    aiChatEnabled,
    aiContactFormEnabled,
    isOpen,
    loadDrawer,
    handleStateChange,
    handleDrawerClose,
    handleFirstHover,
    chat: readonly(globalChatInstance),
    openAiChat,
    sendMessage,
  };
};
